import {
  AfterViewInit,
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewChild,
  OnDestroy,
  NgZone,
} from '@angular/core';

@Component({
  selector: 'app-multi-thumb-slider',
  templateUrl: './multi-thumb-slider.component.html',
  styleUrls: ['./multi-thumb-slider.component.scss'],
})
export class MultiThumbSliderComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() min = 0;
  @Input() max = 250;
  @Input() step = 1;
  @Input() values: number[] = [0, 0, 0, 0];
  @Input() label = '';
  @Input() mode: 'symmetric' | 'low-to-high' | 'high-to-low' = 'symmetric';
  @Input() disabled = false;
  @Output() valuesChange = new EventEmitter<number[]>();
  @ViewChild('track', { static: false }) trackEl: ElementRef<HTMLElement>;

  trackGradient = '';
  draggingIndex: number | null = null;
  private thumbSizePx = 34; // default fallback; updated from DOM in ngAfterViewInit

  private boundMouseMove = this.onMouseMove.bind(this);
  private boundMouseUp = this.onMouseUp.bind(this);
  private boundTouchMove = this.onTouchMove.bind(this);
  private boundTouchEnd = this.onMouseUp.bind(this);

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    const thumb = this.trackEl?.nativeElement.querySelector('.thumb-label') as HTMLElement;
    if (thumb) {
      this.thumbSizePx = thumb.offsetWidth;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.values || changes.min || changes.max || changes.mode) {
      this.updateTrackGradient();
    }
  }

  ngOnDestroy(): void {
    this.removeGlobalListeners();
  }

  trackByIndex(index: number): number {
    return index;
  }

  onTrackMouseDown(event: MouseEvent): void {
    if (this.disabled) {
      return;
    }
    event.preventDefault();
    const index = this.getClosestThumbIndex(event.clientX);
    this.draggingIndex = index;
    this.updateValueFromClient(event.clientX, index);
    this.addGlobalListeners('mouse');
  }

  onTrackTouchStart(event: TouchEvent): void {
    if (this.disabled) {
      return;
    }
    event.preventDefault();
    const index = this.getClosestThumbIndex(event.touches[0].clientX);
    this.draggingIndex = index;
    this.updateValueFromClient(event.touches[0].clientX, index);
    this.addGlobalListeners('touch');
  }

  getThumbPosition(value: number): number {
    return ((value - this.min) / (this.max - this.min)) * 100;
  }

  getThumbLeft(value: number): string {
    const ratio = (value - this.min) / (this.max - this.min);
    const half = this.thumbSizePx / 2;
    return `calc(${ratio} * (100% - ${this.thumbSizePx}px) + ${half}px)`;
  }

  getNearPosition(index: number): string | null {
    const threshold = this.step * 10;
    const val = this.values[index];
    const nearPrev = index > 0 && Math.abs(val - this.values[index - 1]) <= threshold;
    const nearNext = index < this.values.length - 1 && Math.abs(val - this.values[index + 1]) <= threshold;

    if (!nearPrev && !nearNext) {
      return null;
    }
    if (nearNext) {
      return 'below';
    }
    return 'above';
  }

  private getClosestThumbIndex(clientX: number): number {
    const track = this.trackEl.nativeElement;
    const rect = track.getBoundingClientRect();
    const percent = ((clientX - rect.left) / rect.width) * 100;

    let closestIndex = 0;
    let closestDist = Infinity;
    this.values.forEach((val, i) => {
      const pos = this.getThumbPosition(val);
      const dist = Math.abs(pos - percent);
      if (dist < closestDist) {
        closestDist = dist;
        closestIndex = i;
      }
    });
    return closestIndex;
  }

  private updateValueFromClient(clientX: number, index: number): void {
    const track = this.trackEl.nativeElement;
    const rect = track.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    let newValue = this.min + ratio * (this.max - this.min);
    newValue = Math.round(newValue / this.step) * this.step;
    const decimals = (this.step.toString().split('.')[1] || '').length;
    newValue = parseFloat(newValue.toFixed(decimals));
    newValue = Math.max(this.min, Math.min(this.max, newValue));

    if (index > 0 && newValue <= this.values[index - 1]) {
      newValue = this.values[index - 1] + this.step;
    }
    if (index < this.values.length - 1 && newValue >= this.values[index + 1]) {
      newValue = this.values[index + 1] - this.step;
    }

    this.values[index] = newValue;
    this.updateTrackGradient();
    this.valuesChange.emit([...this.values]);
  }

  private onMouseMove(event: MouseEvent): void {
    if (this.draggingIndex === null) {
      return;
    }
    this.ngZone.run(() => {
      this.updateValueFromClient(event.clientX, this.draggingIndex);
    });
  }

  private onTouchMove(event: TouchEvent): void {
    if (this.draggingIndex === null) {
      return;
    }
    event.preventDefault();
    this.ngZone.run(() => {
      this.updateValueFromClient(event.touches[0].clientX, this.draggingIndex);
    });
  }

  private onMouseUp(): void {
    this.draggingIndex = null;
    this.removeGlobalListeners();
  }

  private addGlobalListeners(type: 'mouse' | 'touch'): void {
    this.ngZone.runOutsideAngular(() => {
      if (type === 'mouse') {
        document.addEventListener('mousemove', this.boundMouseMove);
        document.addEventListener('mouseup', this.boundMouseUp);
      } else {
        document.addEventListener('touchmove', this.boundTouchMove, { passive: false });
        document.addEventListener('touchend', this.boundTouchEnd);
      }
    });
  }

  private removeGlobalListeners(): void {
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);
    document.removeEventListener('touchmove', this.boundTouchMove);
    document.removeEventListener('touchend', this.boundTouchEnd);
  }

  private updateTrackGradient(): void {
    const tier3 = '#ef5244';
    const tier2 = '#fbb465';
    const tier1 = '#537d8a';

    if (this.mode === 'low-to-high' && this.values.length === 2) {
      const p1 = this.getThumbPosition(this.values[0]);
      const p2 = this.getThumbPosition(this.values[1]);
      this.trackGradient = `linear-gradient(to right,
        ${tier3} 0%, ${tier3} ${p1}%,
        ${tier2} ${p1}%, ${tier2} ${p2}%,
        ${tier1} ${p2}%, ${tier1} 100%)`;
    } else if (this.mode === 'high-to-low' && this.values.length === 2) {
      const p1 = this.getThumbPosition(this.values[0]);
      const p2 = this.getThumbPosition(this.values[1]);
      this.trackGradient = `linear-gradient(to right,
        ${tier1} 0%, ${tier1} ${p1}%,
        ${tier2} ${p1}%, ${tier2} ${p2}%,
        ${tier3} ${p2}%, ${tier3} 100%)`;
    } else {
      const p1 = this.getThumbPosition(this.values[0]);
      const p2 = this.getThumbPosition(this.values[1]);
      const p3 = this.getThumbPosition(this.values[2]);
      const p4 = this.getThumbPosition(this.values[3]);
      this.trackGradient = `linear-gradient(to right,
        ${tier3} 0%, ${tier3} ${p1}%,
        ${tier2} ${p1}%, ${tier2} ${p2}%,
        ${tier1} ${p2}%, ${tier1} ${p3}%,
        ${tier2} ${p3}%, ${tier2} ${p4}%,
        ${tier3} ${p4}%, ${tier3} 100%)`;
    }
  }
}
